import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';

import {
  getAgentQualityReport,
  getBehaviorAuditReport,
  getCsatReport,
  getSlaReport,
  getWorkloadReport,
} from '@/api/cs/reports.api';
import { CsErrorCard, CsLoading, SELECT_CLS } from '@/components/cs/PageStates';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Tabs } from '@/components/ui/Tabs';
import { cn } from '@/lib/cn';

/**
 * Operations → Reports (spec §3.8). Sub-tabs: Workload | SLA | Agent
 * quality | Behavior audit (>2σ actors highlighted) | CSAT (explicit
 * Retently stub — the endpoint says so rather than faking zeros).
 */

type ReportId = 'workload' | 'sla' | 'quality' | 'behavior' | 'csat';

const REPORT_TABS: { id: ReportId; label: string }[] = [
  { id: 'workload', label: 'Workload' },
  { id: 'sla', label: 'SLA' },
  { id: 'quality', label: 'Agent quality' },
  { id: 'behavior', label: 'Behavior audit' },
  { id: 'csat', label: 'CSAT' },
];

export function ReportsTab() {
  const [report, setReport] = useState<ReportId>('workload');

  return (
    <div className="space-y-4">
      <Tabs
        tabs={REPORT_TABS}
        activeTab={report}
        onChange={(id) => {
          setReport(id as ReportId);
        }}
      />
      {report === 'workload' ? <WorkloadReportView /> : null}
      {report === 'sla' ? <SlaReportView /> : null}
      {report === 'quality' ? <AgentQualityView /> : null}
      {report === 'behavior' ? <BehaviorAuditView /> : null}
      {report === 'csat' ? <CsatView /> : null}
    </div>
  );
}

function WindowPicker({ days, onChange }: { days: number; onChange: (days: number) => void }) {
  return (
    <select
      aria-label="Report window"
      value={String(days)}
      onChange={(e) => {
        onChange(Number(e.target.value));
      }}
      className={SELECT_CLS}
    >
      <option value="7">Last 7 days</option>
      <option value="30">Last 30 days</option>
      <option value="90">Last 90 days</option>
    </select>
  );
}

// ── Workload ────────────────────────────────────────────────────────────

function WorkloadReportView() {
  const query = useQuery({ queryKey: ['cs', 'reports', 'workload'], queryFn: getWorkloadReport });
  if (query.isLoading) return <CsLoading />;
  if (query.error || !query.data) {
    return (
      <CsErrorCard title="Couldn't load the workload report" error={query.error} fallback="Unknown error" />
    );
  }
  const data = query.data;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <SimpleTable
        title="Open tickets by assignee"
        rows={data.byAssignee.map((r) => ({
          key: r.membershipId ?? 'unassigned',
          label: r.name ?? 'Unassigned',
          value: r.openTickets,
        }))}
      />
      <SimpleTable
        title="Open tickets by group"
        rows={data.byGroup.map((r) => ({
          key: r.groupId ?? 'none',
          label: r.groupName ?? 'No group',
          value: r.openTickets,
        }))}
      />
      <SimpleTable
        title="All tickets by status"
        rows={data.byStatus.map((r) => ({ key: r.status, label: r.status, value: r.count }))}
      />
    </div>
  );
}

function SimpleTable({
  title,
  rows,
}: {
  title: string;
  rows: { key: string; label: string; value: number }[];
}) {
  return (
    <Card className="p-4">
      <h3 className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">{title}</h3>
      {rows.length === 0 ? (
        <p className="mt-2 text-[13px] text-kw-muted">No data.</p>
      ) : (
        <ul className="mt-2 divide-y divide-kw-border">
          {rows.map((r) => (
            <li key={r.key} className="flex items-center justify-between py-1.5 text-[13px]">
              <span className="text-kw-shark">{r.label}</span>
              <span className="font-medium tabular-nums text-kw-shark">{String(r.value)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── SLA ─────────────────────────────────────────────────────────────────

function SlaReportView() {
  const [days, setDays] = useState(7);
  const query = useQuery({ queryKey: ['cs', 'reports', 'sla', days], queryFn: () => getSlaReport(days) });
  if (query.isLoading) return <CsLoading />;
  if (query.error || !query.data) {
    return <CsErrorCard title="Couldn't load the SLA report" error={query.error} fallback="Unknown error" />;
  }
  const data = query.data;
  const stats: { label: string; value: string }[] = [
    { label: 'Tickets in window', value: String(data.totalTickets) },
    { label: 'Got a first reply', value: String(data.repliedTickets) },
    {
      label: 'Median first reply',
      value: data.medianFirstReplyMinutes === null ? '—' : `${String(data.medianFirstReplyMinutes)}m`,
    },
    {
      label: 'P90 first reply',
      value: data.p90FirstReplyMinutes === null ? '—' : `${String(data.p90FirstReplyMinutes)}m`,
    },
    { label: 'Nudges posted', value: String(data.nudgeCount) },
    { label: 'SLA breaches', value: String(data.breachCount) },
  ];
  return (
    <div className="space-y-3">
      <WindowPicker days={days} onChange={setDays} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {stats.map((s) => (
          <Card key={s.label} className="p-4">
            <p className="text-[12px] text-kw-muted">{s.label}</p>
            <p className="mt-1 text-[22px] font-semibold tabular-nums text-kw-shark">{s.value}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── Agent quality ───────────────────────────────────────────────────────

function AgentQualityView() {
  const [days, setDays] = useState(30);
  const query = useQuery({
    queryKey: ['cs', 'reports', 'agent-quality', days],
    queryFn: () => getAgentQualityReport(days),
  });
  if (query.isLoading) return <CsLoading />;
  if (query.error || !query.data) {
    return (
      <CsErrorCard
        title="Couldn't load the agent-quality report"
        error={query.error}
        fallback="Unknown error"
      />
    );
  }
  const data = query.data;
  return (
    <div className="space-y-3">
      <WindowPicker days={days} onChange={setDays} />
      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[520px] text-left">
          <thead>
            <tr className="border-b border-kw-border text-[12px] uppercase tracking-wide text-kw-muted">
              <th className="px-4 py-2.5 font-medium">Agent</th>
              <th className="px-4 py-2.5 font-medium">Replies sent</th>
              <th className="px-4 py-2.5 font-medium">Tickets resolved</th>
              <th className="px-4 py-2.5 font-medium">Reopens</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-kw-border">
            {data.agents.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-[13px] text-kw-muted">
                  No agent activity in this window.
                </td>
              </tr>
            ) : (
              data.agents.map((a) => (
                <tr key={a.membershipId} className="text-[13px] text-kw-shark">
                  <td className="px-4 py-2.5 font-medium">{a.name ?? a.membershipId}</td>
                  <td className="px-4 py-2.5 tabular-nums">{String(a.repliesSent)}</td>
                  <td className="px-4 py-2.5 tabular-nums">{String(a.ticketsResolved)}</td>
                  <td className="px-4 py-2.5 text-kw-muted">
                    {a.reopenCount === null ? 'n/a' : String(a.reopenCount)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
      <p className="text-[12px] text-kw-muted">{data.note}</p>
    </div>
  );
}

// ── Behavior audit ──────────────────────────────────────────────────────

function BehaviorAuditView() {
  const [days, setDays] = useState(30);
  const query = useQuery({
    queryKey: ['cs', 'reports', 'behavior-audit', days],
    queryFn: () => getBehaviorAuditReport(days),
  });
  if (query.isLoading) return <CsLoading />;
  if (query.error || !query.data) {
    return (
      <CsErrorCard title="Couldn't load the behavior audit" error={query.error} fallback="Unknown error" />
    );
  }
  const data = query.data;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <WindowPicker days={days} onChange={setDays} />
        <p className="text-[12px] text-kw-muted">
          Manual reassignments per actor. Mean {data.mean === null ? '—' : data.mean.toFixed(1)} · σ{' '}
          {data.stdDev === null ? '—' : data.stdDev.toFixed(1)} — actors more than 2σ above the mean are
          flagged.
        </p>
      </div>
      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[480px] text-left">
          <thead>
            <tr className="border-b border-kw-border text-[12px] uppercase tracking-wide text-kw-muted">
              <th className="px-4 py-2.5 font-medium">Actor</th>
              <th className="px-4 py-2.5 font-medium">Manual reassignments</th>
              <th className="px-4 py-2.5 font-medium">Flag</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-kw-border">
            {data.actors.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-[13px] text-kw-muted">
                  No manual reassignments in this window.
                </td>
              </tr>
            ) : (
              data.actors.map((a) => (
                <tr
                  key={a.membershipId}
                  className={cn('text-[13px] text-kw-shark', a.flagged && 'bg-kw-danger-bg/50')}
                >
                  <td className="px-4 py-2.5 font-medium">{a.name ?? a.membershipId}</td>
                  <td className="px-4 py-2.5 tabular-nums">{String(a.manualReassignments)}</td>
                  <td className="px-4 py-2.5">
                    {a.flagged ? <Badge variant="danger">&gt; 2σ above mean</Badge> : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ── CSAT stub ───────────────────────────────────────────────────────────

function CsatView() {
  const query = useQuery({ queryKey: ['cs', 'reports', 'csat'], queryFn: getCsatReport });
  if (query.isLoading) return <CsLoading />;
  if (query.error || !query.data) {
    return <CsErrorCard title="Couldn't load the CSAT report" error={query.error} fallback="Unknown error" />;
  }
  return (
    <Card className="flex items-start gap-3 p-6">
      <Info className="mt-0.5 size-5 shrink-0 text-kw-cerulean" />
      <div>
        <p className="text-[14px] font-medium text-kw-shark">CSAT is not wired up yet</p>
        <p className="mt-1 text-[13px] text-kw-muted">{query.data.message}</p>
      </div>
    </Card>
  );
}
